import { Progress } from '@/components/ui/progress'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CheckCircle2, XCircle } from 'lucide-react'
import { ProcessingResult, SimpleProcessingProgress } from '@/types'
import { EarningsCreationResults } from '@/services/stagingEarningsProcessingService'

interface EarningsCreationStepProps {
  isProcessing: boolean
  progress: SimpleProcessingProgress | null
  result: ProcessingResult | EarningsCreationResults | null
  totalRows: number
}

function isEarningsCreationResults(result: ProcessingResult | EarningsCreationResults | null): result is EarningsCreationResults {
  return result !== null && 'stagingId' in result
}

function getResultStats(result: ProcessingResult | EarningsCreationResults | null) {
  if (!result) return null

  if (isEarningsCreationResults(result)) {
    return {
      successCount: result.successCount,
      errorCount: result.errorCount,
      skippedCount: result.skippedCount || 0,
      totalGross: result.totalGrossSalary,
      errors: result.errors || []
    }
  } else {
    return {
      successCount: result.successCount,
      errorCount: result.errorCount,
      skippedCount: 0,
      totalGross: result.summary?.totalGross || 0,
      errors: result.errors || []
    }
  }
}

export function EarningsCreationStep({
  isProcessing,
  progress,
  result
}: EarningsCreationStepProps) {
  const stats = getResultStats(result)

  if (isProcessing) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Skapar inkomstposter...
        </p>
        {progress && (
          <div>
            <Progress value={progress.percentage} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {progress.processedRows} av {progress.totalRows} rader
            </p>
          </div>
        )}
      </div>
    )
  }

  // Results state
  if (result && stats) {
    return (
      <div className="space-y-8">
        {/* Success Message */}
        <div className="text-center py-8">
          <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-green-900 mb-2">Uppladdningen är klar!</h2>
          <p className="text-lg text-gray-700">
            {stats.successCount} inkomster skapades framgångsrikt
            {stats.skippedCount > 0 && `, ${stats.skippedCount} hoppades över`}
            {stats.errorCount > 0 && `, ${stats.errorCount} fel uppstod`}.
          </p>
        </div>

        {/* Errors Section */}
        {stats.errors.length > 0 && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <XCircle className="h-5 w-5" />
                Fel som uppstod ({stats.errors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rad</TableHead>
                      <TableHead>Förare</TableHead>
                      <TableHead>Fel</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>{error.rowIndex + 1}</TableCell>
                        <TableCell>
                          {error.driverName || 'Okänd'}
                          {error.driverId && (
                            <div className="text-xs text-muted-foreground">
                              ID: {error.driverId}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-red-700 bg-red-50">
                            {error.error}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  return null
}
